import { Option as RadioOption } from "@storeblocks/radio";
import { Option as SelectOption } from "@storeblocks/select";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { TextManager } from "Components/TextContext";
import { NotSuperReasonCabinContents } from "Features/CheckPrice/Products/RiskProducts/HouseProducts/CabinContents/cabinContentsTypes";
import { removeWhitespaceOptional } from "Formatting/index";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { ProductBackendName } from "../../productTypes";
import { NotSuperReasonCabin } from "./Cabin/cabinTypes";
import { CabinContentsSum } from "./CabinContents/cabinContentsConstants";
import { RadioButtonOptionFMS } from "./House/ProductDetails/houseProductDetailsInterfaces";
import { NotSuperReasonHouse } from "./House/houseTypes";
import { HouseContentsSum } from "./HouseContents/houseContentsConstants";

export const getOptions = (
  field: any,
  hintTexts: any,
  lang: string,
): RadioOption[] =>
  field.options.map((option: RadioButtonOptionFMS) => {
    return {
      id: option.id ?? option.value,
      label: option.label[lang],
      value: option.value,
      hint: option.hint ? option.hint[lang] : "",
      hintProps: {
        maxLines: 4,
        readLessLabel: `${hintTexts.readLess[lang]}`,
        readMoreLabel: `${hintTexts.readMore[lang]}`,
      },
    };
  });

export function getSelectOptions(
  defaultLabel: string,
  field: any,
  lang: string,
): SelectOption[] {
  const options = field.map((option: SelectOption | any) => ({
    label: option.label[lang],
    value: option.value,
  }));
  return [{ label: defaultLabel, value: "" }, ...options];
}

export const getMonthlyPrice = (
  deductable: string,
  productCoverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
): number => {
  const relevantCoverage = coverages.find(
    ([coverage]) => coverage === productCoverage,
  );
  const priceCalculationEntry = relevantCoverage?.[1];
  const price = priceCalculationEntry?.prices.find(
    ({ value }) => value === deductable,
  );
  return price?.monthlyPricePrecise ?? 0;
};

export const getCoverageAmountErrorMessage = (
  product: ProductBackendName,
  isFullRental: boolean,
): string => {
  const { getText } = TextManager;

  switch (product) {
    case ProductBackendName.HOUSE:
      return isFullRental
        ? getText(
            "houseform.alertdescription.contentsinsurancesum-toohighrental",
          )
        : getText("houseform.alertdescription.contentsinsurancesum-toohigh");
    case ProductBackendName.HOUSE_CONTENTS:
      return isFullRental
        ? getText(
            "housecontentsform.alertdescription.coverageamount-toohighrental",
          )
        : getText("housecontentsform.alertdescription.coverageamount-toohigh");
    default:
      return "";
  }
};

export const formatCoverageAmount = (inputValue: string): string => {
  return inputValue.split(" ").join("");
};

export const getNoSuperAlertDescriptions = (
  notSuperReason:
    | NotSuperReasonHouse
    | NotSuperReasonCabin
    | NotSuperReasonCabinContents
    | null,
): string | undefined => {
  const { getText } = TextManager;

  switch (notSuperReason) {
    case NotSuperReasonHouse.FULL_RENTAL:
      return getText("houseform.alerttitle.fullrental");
    case NotSuperReasonHouse.HAS_DAMAGE_FROM_PESTS:
      return getText("houseform.alerttitle.pestsdamagednosuper");
    case NotSuperReasonHouse.OLD_HOUSE_ROOF_NOT_CHANGED:
      return getText("houseform.alerttitle.oldhouseroofnotchanged");
    case NotSuperReasonHouse.OLD_HOUSE_WET_AREAS_NOT_RENOVATED:
      return getText("houseform.alerttitle.oldhousewetareasnotrenovated");
    case NotSuperReasonHouse.TOO_OLD_HOUSE:
      return getText("houseform.alerttitle.oldhouse");
    case NotSuperReasonCabin.HAS_CRAWL_SPACE:
      return getText("cabinform.alerttitle.crawlspace");
    case NotSuperReasonCabin.HAS_DAMAGE_FROM_PESTS:
      return getText("cabinform.alerttitle.pestsdamage");
    case NotSuperReasonCabin.TOO_OLD_CABIN:
      return getText("cabinform.alerttitle.oldcabin");
    case NotSuperReasonCabinContents.HAS_RENTAL:
      return getText("cabincontentsform.alerttitle.hasrental");
    default:
      return undefined;
  }
};

export const filterCoveragesOnCoverage = (
  coverageTypes: InsuranceCoverage[],
  coverages: CoveragePriceCalculation[],
): CoveragePriceCalculation[] => {
  const includesCoverage = ([coverage]: CoveragePriceCalculation): boolean =>
    coverageTypes.includes(coverage);

  return coverages.filter(includesCoverage);
};

export const CABIN_CODE = "4";
export const CABIN_VALUE = "30000";
export const filterCabinDeductibleNotAvailableForCustomer = (
  code: "4",
  value: "30000",
  coverages: CoveragePriceCalculation[],
): CoveragePriceCalculation[] => {
  return coverages.map((coverage) => {
    const coveragePriceCalculation = coverage[1];
    const prices = coveragePriceCalculation.prices.filter(
      (price) => price.code !== code && price.value !== value,
    );
    const compositions = coveragePriceCalculation.compositions;
    return [
      coverage[0],
      {
        ...coveragePriceCalculation,
        prices,
        compositions,
      },
    ];
  });
};

export const getCoveragesFilteredOnStandardSuper = (
  superCoverage: boolean,
  insuranceCoverageStandard: InsuranceCoverage,
): CoveragePriceCalculation[] => {
  const coverages = useCoveragePriceCalculations();
  const standardCoverages = coverages.filter(([coverage]) => {
    return coverage === insuranceCoverageStandard;
  });
  return superCoverage ? coverages : standardCoverages;
};

export const isCoverageSumTooHigh = (
  product: ProductBackendName,
  rental: boolean,
  coverageSum?: string,
): boolean => {
  const coverageSumWithoutWhiteSpace = removeWhitespaceOptional(coverageSum);
  const maxContentsCoverage = maxContentsSum(product, rental);
  return Number(coverageSumWithoutWhiteSpace) > maxContentsCoverage;
};

export const isCoverageSumTooLow = (
  product: ProductBackendName,
  coverageSum?: string,
): boolean => {
  const coverageSumWithoutWhiteSpace = removeWhitespaceOptional(coverageSum);
  const minContentsCoverage = minContentsSum(product);
  return Number(coverageSumWithoutWhiteSpace) < minContentsCoverage;
};

export const isCoverageSumValid = (
  product: ProductBackendName,
  rental: boolean,
  coverageSum?: string,
): boolean =>
  !isCoverageSumTooHigh(product, rental, coverageSum) &&
  !isCoverageSumTooLow(product, coverageSum);

export const maxContentsSum = (
  product: ProductBackendName,
  rental: boolean,
): number => {
  switch (product) {
    case ProductBackendName.HOUSE_CONTENTS:
    case ProductBackendName.HOUSE:
      return rental
        ? HouseContentsSum.UPPER_LIMIT_HOUSE_RENTED_OUT
        : HouseContentsSum.UPPER_LIMIT;
    case ProductBackendName.CABIN_CONTENTS:
      return CabinContentsSum.UPPER_LIMIT;
    default:
      return 0;
  }
};

export const minContentsSum = (product: ProductBackendName): number => {
  switch (product) {
    case ProductBackendName.HOUSE_CONTENTS:
    case ProductBackendName.HOUSE:
      return HouseContentsSum.LOWER_LIMIT;
    case ProductBackendName.CABIN_CONTENTS:
      return CabinContentsSum.LOWER_LIMIT;
    default:
      return 0;
  }
};
